import PropTypes from 'prop-types';

/**
 * A reusable tab component with customizable tabs and content.
 *
 * @component
 * @param {Object} props - Component props
 * @param {Array<{label: string, value: string}>} props.tabs - Array of tab objects with label and value
 * @param {string} props.activeTab - The currently active tab value
 * @param {Function} props.onTabChange - Callback function when tab is changed, receives the new tab value
 * @param {React.ReactNode} props.children - Content to be rendered in the tab panel
 *
 * @example
 * // Basic usage
 * const [activeTab, setActiveTab] = useState('tab1');
 *
 * const tabsConfig = [
 *   { label: 'Tab 1', value: 'tab1' },
 *   { label: 'Tab 2', value: 'tab2' },
 *   { label: 'Tab 3', value: 'tab3' },
 * ];
 *
 * return (
 *   <Tabs
 *     tabs={tabsConfig}
 *     activeTab={activeTab}
 *     onTabChange={setActiveTab}
 *   >
 *     {activeTab === 'tab1' && <ComponentA />}
 *     {activeTab === 'tab2' && <ComponentB />}
 *     {activeTab === 'tab3' && <ComponentC />}
 *   </Tabs>
 * );
 *
 * @example
 * // With conditional rendering
 * <Tabs tabs={tabs} activeTab={current} onTabChange={handleChange}>
 *   {current === 'overview' ? <Overview /> : <Details />}
 * </Tabs>
 */
export default function Tabs({ tabs, activeTab, onTabChange, children, childClassName='', containerClassName='' }) {
  return (
    <div className={`bg-white rounded-lg shadow-md border border-gray-200 ${containerClassName}`}>
      <div className="border-b border-gray-200">
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.value}
              onClick={() => onTabChange(tab.value)}
              className={`px-6 py-3 text-base font-semibold transition-all duration-200 ${
                activeTab === tab.value
                  ? 'text-orange-600 border-b-2 border-orange-400'
                  : 'text-gray-600 border-b-2 border-transparent hover:text-gray-900 hover:bg-gray-50'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className={`p-6 ${childClassName}`}>{children}</div>
    </div>
  );
}

Tabs.propTypes = {
  tabs: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    })
  ).isRequired,
  childClassName: PropTypes.string,
  containerClassName: PropTypes.string,
  activeTab: PropTypes.string.isRequired,
  onTabChange: PropTypes.func.isRequired,
  children: PropTypes.node,
};
